import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchDashboard, createWebSocket } from '../services/api';

const ENV_STORAGE_KEY = 'coynitt-dashboard-environment';

const readPersistedEnv = () => {
  if (typeof window === 'undefined') return 'mainnet';
  const v = window.localStorage.getItem(ENV_STORAGE_KEY);
  return v === 'testnet' ? 'testnet' : 'mainnet';
};

// Mock data shown until the first /api/dashboard response lands.
const FALLBACK_DATA = {
  transactions: { summary: {}, corridors: [], routes: [], timeseries: [] },
  wallets: [],
  compliance: { summary: {}, recentFlags: [] },
  agentReports: [],
};

export const useDashboard = () => {
  const [data, setData] = useState(FALLBACK_DATA);
  const [environment, setEnvironmentState] = useState(readPersistedEnv);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const wsRef = useRef(null);

  const setEnvironment = useCallback((next) => {
    if (next !== 'mainnet' && next !== 'testnet') return;
    setEnvironmentState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ENV_STORAGE_KEY, next);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchDashboard(environment);
      setData(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.warn('[useDashboard] /api/dashboard failed, keeping prior data:', err.message);
      setError(null); // soft-fail: don't blow up the UI
    } finally {
      setLoading(false);
    }
  }, [environment]);

  // Refresh on mount AND on environment change.
  useEffect(() => { refresh(); }, [refresh]);

  // WebSocket — best-effort; degrades silently to polling on Vercel where WS isn't supported.
  useEffect(() => {
    let ws;
    try {
      ws = createWebSocket();
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const { event: eventType } = JSON.parse(event.data);
          if (['transaction:new', 'transaction:updated', 'compliance:flag_updated'].includes(eventType)) {
            refresh();
          }
        } catch {/* malformed payload */}
      };
      ws.onerror = () => {/* expected on Vercel */};
    } catch {/* WebSocket not available */}
    return () => { try { ws?.close(); } catch {} };
  }, [refresh]);

  // Poll every 30s as a backstop.
  useEffect(() => {
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [refresh]);

  return { data, environment, setEnvironment, loading, error, lastUpdated, refresh };
};
